import React, { FC, useEffect, RefObject, useCallback, Suspense } from "react"
import { useBoolean } from "../../../Hooks/useBoolean"
import { uploadData } from "../../../Actions/API/Data/DataActions"
import { useDispatch } from "react-redux"
import { randID } from "../../../Helpers/Functions/random"
import { MainThunkDispatch } from "../../../Reducers"
import type {
    KeyboardShortcut,
    MLActionProcessor,
    PrelabelResponse,
    SchemaFormData
} from "resha"
import { failMessage } from "../../../Actions/Client/MessagesActions"
import { ErrorBoundary } from "../ErrorBoundary/ErrorBoundary"

const ReshaDataPointEditApp = React.lazy(async () => {
    return import("resha").then(resha => ({ default: resha.DataPointEditApp }))
})

export interface DataPointEditAppProps {
    input: SchemaFormData
    output: SchemaFormData
    urlSigner: (url: string) => Promise<string>
    mlActionProcessor: MLActionProcessor
    toolbarRef: RefObject<HTMLElement>
    footerRef: RefObject<HTMLDivElement>
    saveButtonLabel: string
    resetAnnotation: boolean
    uploadPathPrefix: string
    onSave: (output: unknown) => Promise<void>
    prelabels?: PrelabelResponse
    errorMessage: string
    fallback: JSX.Element
    onEdit?: () => void
    onShortcutList?: (shortcutList: {
        [key: string]: [string, KeyboardShortcut][]
    }) => void
}

export const DataPointEditApp: FC<DataPointEditAppProps> = ({
    input,
    output,
    urlSigner,
    mlActionProcessor,
    toolbarRef,
    footerRef,
    saveButtonLabel,
    resetAnnotation,
    uploadPathPrefix,
    onSave,
    prelabels,
    errorMessage,
    fallback,
    onEdit,
    onShortcutList
}) => {
    const dispatch = useDispatch<MainThunkDispatch>()
    const [isSaving, startSaving, endSaving] = useBoolean(false)
    const handleSave = useCallback(
        (value: Promise<unknown>) => {
            startSaving()
            value.then(onSave).finally(endSaving)
        },
        [startSaving, onSave, endSaving]
    )
    const handleUploadBlob = useCallback(
        (blob: Blob) =>
            dispatch(uploadData(blob, `${uploadPathPrefix}/${randID()}`)),
        [dispatch, uploadPathPrefix]
    )
    const handleError = useCallback(
        (message: string, error?: unknown) =>
            dispatch(failMessage(message, error)),
        [dispatch]
    )

    useEffect(() => {
        // hide intercom chat launcher to not cover submit button
        window.Intercom?.("update", { hide_default_launcher: true })

        return () => {
            window.Intercom?.("update", { hide_default_launcher: false })
        }
    }, [])

    return (
        <ErrorBoundary message={errorMessage}>
            <Suspense fallback={fallback}>
                <ReshaDataPointEditApp
                    input={input}
                    output={output}
                    isSaving={isSaving}
                    urlSigner={urlSigner}
                    blobUploader={handleUploadBlob}
                    mlActionProcessor={mlActionProcessor}
                    toolbarRef={toolbarRef}
                    footerRef={footerRef}
                    saveButtonLabel={saveButtonLabel}
                    resetAnnotation={resetAnnotation}
                    onSave={handleSave}
                    onError={handleError}
                    prelabels={prelabels}
                    onEdit={onEdit}
                    onShortcutList={onShortcutList}
                />
            </Suspense>
        </ErrorBoundary>
    )
}
